import { Injectable } from '@angular/core';
import type { FileEntry } from '@zip.js/zip.js';
import { IZipTreeNode } from '../model/zip-tree-node';

@Injectable({ providedIn: 'root' })
export class ZipExplorerService {
  // `@zip.js/zip.js` faqat shu servis chaqirilganda (dynamic import) yuklanadi —
  // initial bundle'ga qo'shilib, hajmini shishirmasligi uchun.
  private zipModulePromise?: Promise<typeof import('@zip.js/zip.js')>;

  private loadZipModule() {
    if (!this.zipModulePromise) {
      this.zipModulePromise = import('@zip.js/zip.js');
    }
    return this.zipModulePromise;
  }

  /**
   * ZIP faylni brauzerda (diskka saqlamasdan) o'qib, ichidagi fayl/papkalarni
   * daraxt (tree) ko'rinishida qaytaradi. Fayl kontenti hali ochilmaydi —
   * u faqat {@link extractFile} chaqirilganda (lazy) o'qiladi.
   */
  async readTree(
    zipBlob: Blob,
    password?: string | null,
  ): Promise<IZipTreeNode[]> {
    const { BlobReader, ZipReader } = await this.loadZipModule();
    const reader = new ZipReader(new BlobReader(zipBlob), {
      password: password || undefined,
    });
    const entries = await reader.getEntries();
    await reader.close();

    const fileEntries = entries.filter(
      (entry): entry is FileEntry => !entry.directory,
    );

    // Backend ba'zida tashqi ZIP ichiga faqat bitta, parol bilan shifrlangan
    // ichki ZIP (masalan "ProtectedArchive.zip") joylaydi. Bunday holatni
    // aniqlab, uni avtomatik ochamiz (unwrap) — foydalanuvchiga esa ichki
    // ZIP'dagi haqiqiy fayl/papka daraxti ko'rsatiladi.
    if (fileEntries.length === 1 && this.isNestedZipEntry(fileEntries[0])) {
      const innerZipBlob = await this.extractFile(fileEntries[0], password);
      return this.readTree(innerZipBlob, null);
    }

    return this.buildTree(fileEntries);
  }

  private isNestedZipEntry(entry: FileEntry): boolean {
    return entry.filename.toLowerCase().endsWith('.zip');
  }

  /** Tanlangan fayl tugunining kontentini Blob sifatida ochadi. */
  async extractFile(
    entry: FileEntry,
    password?: string | null,
  ): Promise<Blob> {
    const { BlobWriter } = await this.loadZipModule();
    // MIME turi berilmasa, brauzer blob:URL'ni PDF sifatida emas, xom matn
    // sifatida ko'rsatadi — shu uchun kengaytmadan turini aniqlab beramiz.
    return entry.getData(new BlobWriter(this.guessMimeType(entry.filename)), {
      password: password || undefined,
    });
  }

  private guessMimeType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'webp':
        return 'image/webp';
      default:
        return 'application/octet-stream';
    }
  }

  private buildTree(entries: FileEntry[]): IZipTreeNode[] {
    const root: IZipTreeNode[] = [];
    const folderIndex = new Map<string, IZipTreeNode>();

    for (const entry of entries) {
      const segments = entry.filename.split('/').filter(Boolean);
      let currentLevel = root;
      let currentPath = '';

      segments.forEach((segment, index) => {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        const isLastSegment = index === segments.length - 1;

        if (isLastSegment) {
          currentLevel.push({
            name: segment,
            path: currentPath,
            isFolder: false,
            entry,
          });
          return;
        }

        let folderNode = folderIndex.get(currentPath);
        if (!folderNode) {
          folderNode = {
            name: segment,
            path: currentPath,
            isFolder: true,
            children: [],
          };
          folderIndex.set(currentPath, folderNode);
          currentLevel.push(folderNode);
        }
        currentLevel = folderNode.children!;
      });
    }

    return root;
  }
}
